/**
 * Bracket Bundle Builder.
 * Bike -> bracket variant resolution mirrors fitment-bike-selector.liquid.
 * Plate light / locking shim / indicator holder defaults mirror the
 * compatibility logic in block-bracket_blocks--plate-light-upsell.liquid,
 * block-product-info--locking-shim.liquid and
 * block-bracket_blocks--indicator-holders.liquid.
 */
(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Shopify's documented money-format substitution — no library loaded on this page.
  function formatMoney(cents, format) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '£{{amount}}';
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }
    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function tokenMatchVariant(variants, titleDc, fallback) {
    var picked = fallback || (variants && variants[0]) || null;
    (variants || []).forEach(function (v) {
      var tokens = String(v.option1 || '').split('/');
      var matched = tokens.some(function (t) {
        t = t.trim().toLowerCase();
        return t && titleDc.indexOf(t) !== -1;
      });
      if (matched) picked = v;
    });
    return picked;
  }

  // Appends ?variant=<id> so the fetched product page (and so the
  // quick-view modal built from it) opens straight to whichever variant
  // is currently selected here, instead of the product's own default.
  function productUrlWithVariant(url, variantId) {
    if (!url) return url;
    if (!variantId) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variantId;
  }

  // Opens the theme's existing quick-view modal (#PopupModal-quickview,
  // rendered globally in layout/theme.liquid) — same <modal-opener> pattern
  // used by the PDP upsell blocks, no extra JS wiring needed.
  function quickViewButtonHtml(url) {
    if (!url) return '';
    return (
      '<modal-opener data-modal="#PopupModal-quickview" data-handle="' + escapeHtml(url) + '">' +
        '<button type="button" class="fnd-bundle__quickview-btn" aria-haspopup="dialog" aria-label="Quick view">Quick view</button>' +
      '</modal-opener>'
    );
  }

  // Scrollable row of product-image thumbnails under a card. Tapping one
  // opens that actual photo in the shared lightbox (see openLightbox) —
  // not the quick-view product modal, which the separate "Quick view"
  // button still handles. Whether the last thumb needs to look half-cut
  // (there's more to scroll to) can only be known once this is laid out
  // in the DOM — see applyThumbPeek, called after the card is unhidden.
  function thumbsHtml(images) {
    if (!images || !images.length) return '';
    var fullUrls = images.map(function (img) { return img.full; });
    return (
      '<div class="fnd-bundle__thumbs" data-images="' + escapeHtml(JSON.stringify(fullUrls)) + '">' +
        images.map(function (img, i) {
          return (
            '<button type="button" class="fnd-bundle__thumb" data-index="' + i + '" aria-label="View image ' + (i + 1) + '">' +
              '<img src="' + escapeHtml(img.thumb) + '" alt="" loading="lazy">' +
            '</button>'
          );
        }).join('') +
      '</div>'
    );
  }

  // Minimal image + title + price + quick-view card, shared by every
  // accessory suggestion so they all read as the same clean product card.
  // The title links straight to the product page; "Quick view" stays a
  // separate action that opens the modal instead.
  function productCardHtml(opts) {
    var img = opts.image
      ? '<img src="' + escapeHtml(opts.image) + '" alt="" loading="lazy">'
      : '<img alt="" hidden>';
    var titleTag = opts.quickViewUrl
      ? '<a class="fnd-bundle__product-title" href="' + escapeHtml(opts.quickViewUrl) + '">' + escapeHtml(opts.title) + '</a>'
      : '<strong class="fnd-bundle__product-title">' + escapeHtml(opts.title) + '</strong>';
    var showVariant = opts.variant && opts.variant !== 'Default Title' && opts.variant !== opts.title;
    var variantTag = showVariant
      ? '<span class="fnd-bundle__product-variant">' + escapeHtml(opts.variant) + '</span>'
      : '';
    return (
      '<div class="fnd-bundle__product-card">' +
        '<div class="fnd-bundle__product-media">' + img + '</div>' +
        '<div class="fnd-bundle__product-info">' +
          titleTag +
          variantTag +
          '<span class="fnd-bundle__product-price" data-role="price">' + escapeHtml(opts.price || '') + '</span>' +
        '</div>' +
        '<div class="fnd-bundle__product-actions">' + quickViewButtonHtml(productUrlWithVariant(opts.quickViewUrl, opts.variantId)) + '</div>' +
      '</div>' +
      thumbsHtml(opts.images)
    );
  }

  // Second, explicit include/exclude control that sits at the opposite
  // (top-right) corner of the step-head from the checkbox, spelling the
  // same state out in words — "Remove from bundle" / "Add to bundle" —
  // so it reads as an obvious, clickable action rather than relying on
  // the bare checkbox alone.
  function toggleBtnLabel(checked) {
    return checked ? 'Remove from bundle' : 'Add to bundle';
  }

  function toggleBtnHtml(checked) {
    return (
      '<button type="button" class="fnd-bundle__step-toggle-btn" data-included="' + (checked ? 'true' : 'false') + '" aria-pressed="' + (checked ? 'true' : 'false') + '">' +
        toggleBtnLabel(checked) +
      '</button>'
    );
  }

  function setToggleBtnState(btn, checked) {
    if (!btn) return;
    btn.dataset.included = checked ? 'true' : 'false';
    btn.setAttribute('aria-pressed', checked ? 'true' : 'false');
    btn.textContent = toggleBtnLabel(checked);
  }

  // Reuses the site's real checkbox component (.checkbox-input, see
  // assets/component-inputs.css) rather than a bare <input> — checkmarkIconHtml
  // is the theme's own checkmark SVG, read once from a <template> in the section.
  // No text label here: this sits in the step-head as the include/exclude
  // toggle for the whole step, with the state spelled out in the toggle
  // button beside it instead.
  function checkboxHtml(checkmarkIconHtml, role, checked) {
    return (
      '<label class="checkbox-input fnd-bundle__checkbox">' +
        '<input type="checkbox" data-role="' + role + '"' + (checked ? ' checked' : '') + '>' +
        '<div class="checkbox-input__checkmark">' +
          '<div class="checkbox-input__flip">' +
            '<div class="checkbox-input__front"></div>' +
            '<div class="checkbox-input__back">' + checkmarkIconHtml + '</div>' +
          '</div>' +
        '</div>' +
      '</label>'
    );
  }

  function init(root) {
    var dataEl = document.querySelector('script[id^="fnd-bundle-data--"]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[fnd-bundle] could not parse data blob', e);
      return;
    }

    var products = data.products || [];
    var fitments = data.fitments || [];
    var accessories = data.accessories || {};
    var moneyFormat = data.moneyFormat || '£{{amount}}';

    if (!fitments.length) {
      console.warn('[fnd-bundle] no bracket fitment data found');
      return;
    }

    var checkmarkIconTpl = root.querySelector('template[data-fnd-checkbox-icon]');
    var checkmarkIconHtml = checkmarkIconTpl ? checkmarkIconTpl.innerHTML : '';

    var els = {
      brand: root.querySelector('[data-fnd-select="brand"]'),
      model: root.querySelector('[data-fnd-select="model"]'),
      year: root.querySelector('[data-fnd-select="year"]'),
      clearBtn: root.querySelector('[data-fnd-clear]'),
      noMatch: root.querySelector('[data-fnd-no-match]'),
      selectsWrap: root.querySelector('[data-fnd-selects]'),
      bikeLabel: root.querySelector('[data-fnd-bike-label]'),
      bikeStepTitle: root.querySelector('[data-fnd-bike-step-title]'),
      resolved: root.querySelector('[data-fnd-resolved]'),
      resolvedImage: root.querySelector('[data-fnd-resolved-image]'),
      resolvedTitle: root.querySelector('[data-fnd-resolved-title]'),
      resolvedPrice: root.querySelector('[data-fnd-resolved-price]'),
      resolvedVariantRow: root.querySelector('[data-fnd-resolved-variant-row]'),
      resolvedQuickview: root.querySelector('[data-fnd-resolved-quickview]'),
      resolvedThumbs: root.querySelector('[data-fnd-resolved-thumbs]'),
      comingSoon: root.querySelector('[data-fnd-coming-soon]'),
      lightbox: root.querySelector('[data-fnd-lightbox]'),
      lightboxImage: root.querySelector('[data-fnd-lightbox-image]'),
      lightboxClose: root.querySelector('[data-fnd-lightbox-close]'),
      lightboxPrev: root.querySelector('[data-fnd-lightbox-prev]'),
      lightboxNext: root.querySelector('[data-fnd-lightbox-next]'),
      stepBike: root.querySelector('[data-step="bike"]'),
      stepPlateLight: root.querySelector('[data-step="plate-light"]'),
      stepShim: root.querySelector('[data-step="shim"]'),
      stepIndicators: root.querySelector('[data-step="indicators"]'),
      cardPlateLight: root.querySelector('[data-fnd-card="plate-light"]'),
      cardShim: root.querySelector('[data-fnd-card="shim"]'),
      cardIndicators: root.querySelector('[data-fnd-card="indicators"]'),
      stepPlateLightCheckbox: root.querySelector('[data-fnd-step-checkbox="plate-light"]'),
      stepPlateLightToggle: root.querySelector('[data-fnd-step-toggle="plate-light"]'),
      stepShimCheckbox: root.querySelector('[data-fnd-step-checkbox="shim"]'),
      stepShimToggle: root.querySelector('[data-fnd-step-toggle="shim"]'),
      stepIndicatorsCheckbox: root.querySelector('[data-fnd-step-checkbox="indicators"]'),
      stepIndicatorsToggle: root.querySelector('[data-fnd-step-toggle="indicators"]'),
      stepExtras: root.querySelector('[data-step="extras"]'),
      extraShowPlate: root.querySelector('[data-fnd-extra="show-plate"]'),
      extraShowPlateToggle: root.querySelector('[data-fnd-extra-toggle="show-plate"]'),
      extraShowPlatePanel: root.querySelector('[data-fnd-extra-panel="show-plate"]'),
      extraShowPlateAddBtn: root.querySelector('[data-fnd-extra-add="show-plate"]'),
      extraShowPlatePrice: root.querySelector('[data-fnd-extra-price="show-plate"]'),
      extraShowPlateLogo: root.querySelector('[data-fnd-extra-field="show-plate-logo"]'),
      extraShowPlateTop: root.querySelector('[data-fnd-extra-field="show-plate-top"]'),
      extraShowPlateBottom: root.querySelector('[data-fnd-extra-field="show-plate-bottom"]'),
      extraShowPlatePreviewTop: root.querySelector('[data-fnd-extra-preview-top]'),
      extraShowPlatePreviewBottom: root.querySelector('[data-fnd-extra-preview-bottom]'),
      extraShowPlatePreviewLogo: root.querySelector('[data-fnd-extra-preview-logo]'),
      extraPlateExtenders: root.querySelector('[data-fnd-extra="plate-extenders"]'),
      extraPlateExtendersToggle: root.querySelector('[data-fnd-extra-toggle="plate-extenders"]'),
      extraPlateExtendersPrice: root.querySelector('[data-fnd-extra-price="plate-extenders"]'),
      extraRearLight: root.querySelector('[data-fnd-extra="rear-light"]'),
      extraRearLightImage: root.querySelector('[data-fnd-extra-image="rear-light"]'),
      extraRearLightModal: root.querySelector('[data-fnd-extra-modal="rear-light"]'),
      extraRearLightTitle: root.querySelector('[data-fnd-extra-title="rear-light"]'),
      extraRearLightDesc: root.querySelector('[data-fnd-extra-desc="rear-light"]'),
      extraRearLightPrice: root.querySelector('[data-fnd-extra-price="rear-light"]'),
      extraRearLightToggle: root.querySelector('[data-fnd-extra-toggle="rear-light"]'),
      extraRearLightPanel: root.querySelector('[data-fnd-extra-panel="rear-light"]'),
      extraRearLightVariant: root.querySelector('[data-fnd-extra-field="rear-light-variant"]'),
      extraRearLightAddBtn: root.querySelector('[data-fnd-extra-add="rear-light"]'),
      extraRearLightNote: root.querySelector('[data-fnd-extra-note="rear-light"]'),
      extraRearLightFitmentList: root.querySelector('[data-fnd-extra-fitment="rear-light"]'),
      summary: root.querySelector('[data-fnd-summary]'),
      summaryLines: root.querySelector('[data-fnd-summary-lines]'),
      summaryTotal: root.querySelector('[data-fnd-summary-total]'),
      addBtn: root.querySelector('[data-fnd-add-to-cart]'),
      addBtnLabel: root.querySelector('[data-fnd-add-btn-label]')
    };

    var state = {
      bracket: null,
      plateLight: { included: false, variantId: null, sellable: false },
      shim: { included: false, variantId: null, sellable: false },
      indicators: { included: false, type: 'none', variantId: null, sellable: false },
      showPlate: { included: false, variantId: null, topLine: '', bottomLine: '', sellable: false },
      plateExtenders: { included: false, variantId: null, sellable: false },
      rearLight: { included: false, variantId: null, sellable: false }
    };

    // Fired once, the first time a bike resolves to a bracket — a real
    // "they're using it" signal (page views alone don't say whether anyone
    // actually picked a bike), separate from the existing "Bundle Builder
    // Add To Cart" completion event.
    var hasTrackedStart = false;

    function brandsList() {
      return Array.from(new Set(fitments.map(function (f) { return f.brand; }))).sort();
    }

    function modelsFor(brand) {
      return Array.from(new Set(
        fitments.filter(function (f) { return f.brand === brand; }).map(function (f) { return f.model; })
      )).sort();
    }

    function matchesFor(brand, model, year) {
      return fitments.filter(function (f) {
        return f.brand === brand && f.model === model && String(f.year) === String(year);
      });
    }

    // Builds the flat "bracket" shape the rest of the app reads (downstream
    // cards, cart properties, summary) from a resolved product + one of its
    // variants — brand/model/year always come from the original fitment
    // match, kept stable even if the customer switches to a sibling variant.
    function buildBracketState(product, variant, fitment) {
      return {
        brand: fitment.brand,
        model: fitment.model,
        year: fitment.year,
        productTitle: product.title,
        productUrl: product.url,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        priceFormatted: variant.priceFormatted,
        available: variant.available,
        comingSoon: variant.comingSoon,
        image: variant.image,
        hasPlateLightHoles: variant.hasPlateLightHoles,
        plateLightBuiltIn: variant.plateLightBuiltIn,
        badWithLockingShim: variant.badWithLockingShim,
        noIndicatorMounts: variant.noIndicatorMounts,
        canFitStaticIndicatorHolders: variant.canFitStaticIndicatorHolders,
        doesNotWorkWithAnyIndicatorHolders: variant.doesNotWorkWithAnyIndicatorHolders,
        canMountStockIndicators: variant.canMountStockIndicators,
        canMountAftermarketIndicators: variant.canMountAftermarketIndicators,
        rearLightOptions: variant.rearLightOptions
      };
    }

    function resetDownstream() {
      state.bracket = null;
      [els.stepPlateLight, els.stepShim, els.stepIndicators, els.stepExtras].forEach(function (el) {
        el.hidden = true;
        el.classList.remove('is-active', 'is-resolved');
      });
      els.summary.hidden = true;
      els.resolved.hidden = true;
      els.comingSoon.hidden = true;
      els.selectsWrap.hidden = false;
      els.bikeLabel.hidden = true;
      els.bikeStepTitle.textContent = 'Select Your Bike';
    }

    // Only the last thumb in a row that actually overflows its container
    // gets cut to half width — that's the "scroll for more" cue. A row
    // that already shows every image fully stays untouched. This can only
    // be measured once the row is laid out and visible, hence the reset
    // for re-renders and the call site after cards are unhidden.
    function applyThumbPeek() {
      root.querySelectorAll('.fnd-bundle__thumbs').forEach(function (container) {
        var thumbs = container.querySelectorAll('.fnd-bundle__thumb');
        thumbs.forEach(function (t) { t.classList.remove('fnd-bundle__thumb--half'); });
        if (thumbs.length < 2) return;
        if (container.scrollWidth > container.clientWidth + 1) {
          thumbs[thumbs.length - 1].classList.add('fnd-bundle__thumb--half');
        }
      });
      preloadFullImages();
    }

    // The lightbox only ever fetches an image the moment it's tapped, so
    // the first open (and every "new" photo after that) feels slow on a
    // cold connection. Warming the browser cache with these same URLs in
    // the background — as soon as a thumb row is on screen — means the
    // lightbox is usually just reading from cache by the time it's opened.
    // Guarded per-row so re-renders (resize, reselecting the same bracket)
    // don't refetch images already warmed.
    function preloadFullImages() {
      root.querySelectorAll('.fnd-bundle__thumbs[data-images]').forEach(function (container) {
        if (container.dataset.preloaded) return;
        container.dataset.preloaded = 'true';
        var urls;
        try {
          urls = JSON.parse(container.dataset.images);
        } catch (e) {
          return;
        }
        urls.forEach(function (url) { new Image().src = url; });
      });
    }

    function selectBracket(bracket) {
      state.bracket = bracket;
      els.stepBike.classList.add('is-resolved');
      els.clearBtn.hidden = false;
      els.noMatch.hidden = true;

      if (!hasTrackedStart) {
        hasTrackedStart = true;
        if (typeof window.fndTrack === 'function') {
          window.fndTrack('Bundle Builder Started', { bike: bracket.brand + ' ' + bracket.model });
        }
      }

      // Coming-soon bracket: the resolved card above still shows which
      // bike this is, but there's no real product behind the accessory
      // steps yet, so none of that (or a checkout summary) applies —
      // show the notice instead and stop here.
      if (bracket.comingSoon === true) {
        state.plateLight = { included: false, variantId: null, sellable: false };
        state.shim = { included: false, variantId: null, sellable: false };
        state.indicators = { included: false, type: 'none', variantId: null, sellable: false };
        state.rearLight = { included: false, variantId: null, sellable: false };
        if (els.extraRearLight) els.extraRearLight.hidden = true;
        [els.stepPlateLight, els.stepShim, els.stepIndicators, els.stepExtras].forEach(function (el) {
          el.hidden = true;
          el.classList.remove('is-active', 'is-resolved');
        });
        els.summary.hidden = true;
        els.comingSoon.hidden = false;
        saveToStorage(bracket);
        applyThumbPeek();
        return;
      }

      els.comingSoon.hidden = true;

      renderPlateLightCard(bracket);
      renderShimCard(bracket);
      renderIndicatorsCard(bracket);
      renderRearLightExtra(bracket);

      [els.stepPlateLight, els.stepShim, els.stepIndicators, els.stepExtras].forEach(function (el) {
        el.hidden = false;
        el.classList.add('is-active');
      });
      els.summary.hidden = false;

      saveToStorage(bracket);
      updateSummary();
      applyThumbPeek();
    }

    // Replaces the brand/model/year selects with the resolved bracket
    // product once a bike is picked. Always shows every variant of that
    // product as a picker — not just the one whose own bs_models_and_years
    // metafield matched the bike — since sibling options like "with
    // indicator holders" / "no indicator holders" are just other variants
    // of the same product, and only one of them may carry that metafield.
    function renderResolvedPanel(product, fitment) {
      els.selectsWrap.hidden = true;
      els.bikeLabel.hidden = false;
      els.bikeLabel.textContent = fitment.brand + ' ' + fitment.model;
      els.bikeStepTitle.textContent = 'Selected bike:';
      els.resolved.hidden = false;

      function showVariant(variant) {
        if (variant.image) {
          els.resolvedImage.src = variant.image;
          els.resolvedImage.hidden = false;
        } else {
          els.resolvedImage.hidden = true;
        }
        els.resolvedImage.alt = product.title;
        els.resolvedTitle.textContent = product.title;
        els.resolvedTitle.href = product.url;
        els.resolvedPrice.textContent = variant.priceFormatted;
        if (els.resolvedQuickview) els.resolvedQuickview.dataset.handle = productUrlWithVariant(product.url, variant.id);
        if (els.resolvedThumbs) els.resolvedThumbs.innerHTML = variant.comingSoon === true ? '' : thumbsHtml(product.images);

        if (product.variants.length > 1) {
          els.resolvedVariantRow.innerHTML =
            '<span class="fnd-bundle__resolved-options-label">' + escapeHtml(product.optionName || 'Options') + '</span>' +
            '<select class="fnd-bundle__card-select" data-role="resolved-variant" aria-label="Bracket option">' +
              product.variants.map(function (v) {
                return '<option value="' + v.id + '"' + (v.id === variant.id ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' + escapeHtml(v.title) + (v.available ? '' : ' (sold out)') + '</option>';
              }).join('') +
            '</select>';
          els.resolvedVariantRow.querySelector('[data-role="resolved-variant"]').addEventListener('change', function (evt) {
            var v = product.variants.find(function (v) { return v.id === Number(evt.target.value); });
            showVariant(v);
            selectBracket(buildBracketState(product, v, fitment));
          });
        } else {
          var showTitle = variant.title && variant.title !== 'Default Title';
          els.resolvedVariantRow.innerHTML = showTitle
            ? '<span class="fnd-bundle__resolved-variant">' + escapeHtml(variant.title) + '</span>'
            : '';
        }
      }

      var initialVariant = product.variants.find(function (v) { return v.id === fitment.variantId; }) || product.variants[0];
      showVariant(initialVariant);
      selectBracket(buildBracketState(product, initialVariant, fitment));
    }

    function applyMatches(matches) {
      resetDownstream();
      if (!matches.length) {
        els.noMatch.hidden = false;
        return;
      }
      var fitment = matches[0];
      var product = products[fitment.productIndex];
      renderResolvedPanel(product, fitment);
    }

    // Refreshes a rendered product card's image/price/quick-view after a
    // variant switch (shim's rare multi-variant case, indicator type/variant).
    function updateProductCardVisual(card, variant, quickViewUrl) {
      var img = card.querySelector('.fnd-bundle__product-media img');
      if (img) {
        if (variant.image) { img.src = variant.image; img.hidden = false; }
        else { img.hidden = true; }
      }
      var priceEl = card.querySelector('.fnd-bundle__product-price[data-role="price"]');
      if (priceEl) priceEl.textContent = variant.priceFormatted;
      var quickView = card.querySelector('modal-opener');
      if (quickView && quickViewUrl) quickView.dataset.handle = productUrlWithVariant(quickViewUrl, variant.id);
    }

    // The include/exclude checkbox now lives in the step-head (replacing
    // the old numbered badge) rather than buried in the card body, with the
    // state spelled out in the toggle button beside it. Toggling it
    // dims the whole card body (note + product card + thumbs + any select),
    // not just the product-card sliver — the head itself stays full-strength
    // so it's always obvious how to switch it back on.
    function wireStepCheckbox(inputEl, toggleBtn, card, onChange) {
      function apply(checked) {
        inputEl.checked = checked;
        setToggleBtnState(toggleBtn, checked);
        card.classList.toggle('is-excluded', !checked);
        onChange(checked);
      }
      inputEl.addEventListener('change', function () { apply(inputEl.checked); });
      if (toggleBtn) {
        toggleBtn.addEventListener('click', function () { apply(!inputEl.checked); });
      }
    }

    function clearStepCheckbox(checkboxSlot, toggleSlot) {
      checkboxSlot.innerHTML = '';
      if (toggleSlot) toggleSlot.innerHTML = '';
    }

    // ---- Plate light ----
    // If the bracket takes our static light, that's the only option offered —
    // switching to the sticky alternative is a different product entirely and
    // just confuses the picture, so no dropdown here.
    function renderPlateLightCard(bracket) {
      var accessory = accessories.plateLight;
      var card = els.cardPlateLight;
      var checkboxSlot = els.stepPlateLightCheckbox;
      var toggleSlot = els.stepPlateLightToggle;

      if (bracket.plateLightBuiltIn === true) {
        state.plateLight = { included: false, variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML = '<p class="fnd-bundle__card-note">This bracket already has a plate light built in — no separate light needed.</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
        return;
      }

      var staticVariant = accessory && accessory.variants.find(function (v) { return v.isStatic; });

      if (bracket.hasPlateLightHoles === true && staticVariant) {
        state.plateLight = { included: true, variantId: staticVariant.id, sellable: true };
        card.className = 'fnd-bundle__card';
        card.innerHTML =
          '<p class="fnd-bundle__card-note">This bracket is plate light ready — bolt ours straight on.</p>' +
          productCardHtml({
            image: staticVariant.image,
            title: accessory.title,
            variant: staticVariant.title,
            price: staticVariant.priceFormatted,
            quickViewUrl: accessory.productUrl,
            variantId: staticVariant.id,
            images: accessory.images
          });
        checkboxSlot.innerHTML = checkboxHtml(checkmarkIconHtml, 'include', true);
        toggleSlot.innerHTML = toggleBtnHtml(true);
        wireStepCheckbox(checkboxSlot.querySelector('input'), toggleSlot.querySelector('.fnd-bundle__step-toggle-btn'), card, function (checked) {
          state.plateLight.included = checked;
          updateSummary();
        });
      } else {
        var stickyVariant = accessory && accessory.variants.find(function (v) { return !v.isStatic; });
        if (stickyVariant) {
          // Not compatible with the static light, but the sticky variant of
          // the same product still fits — offer it, just not on by default.
          state.plateLight = { included: false, variantId: stickyVariant.id, sellable: true };
          card.className = 'fnd-bundle__card is-excluded';
          card.innerHTML =
            '<p class="fnd-bundle__card-note">This bracket can\'t use our static plate light. Check out the ' +
            '<a href="' + escapeHtml(accessory.productUrl) + '">3M sticky plate light</a> instead.</p>' +
            productCardHtml({
              image: stickyVariant.image,
              title: accessory.title,
              variant: stickyVariant.title,
              price: stickyVariant.priceFormatted,
              quickViewUrl: accessory.productUrl,
              variantId: stickyVariant.id,
              images: accessory.images
            });
          checkboxSlot.innerHTML = checkboxHtml(checkmarkIconHtml, 'include', false);
          toggleSlot.innerHTML = toggleBtnHtml(false);
          wireStepCheckbox(checkboxSlot.querySelector('input'), toggleSlot.querySelector('.fnd-bundle__step-toggle-btn'), card, function (checked) {
            state.plateLight.included = checked;
            updateSummary();
          });
          return;
        }

        state.plateLight = { included: false, variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML =
          '<p class="fnd-bundle__card-note">This bracket can\'t use our static plate light. Check out the ' +
          (accessory ? '<a href="' + escapeHtml(accessory.productUrl) + '">3M sticky plate light</a>' : 'sticky plate light') +
          ' instead.</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
      }
    }

    // ---- Locking shim ----
    function renderShimCard(bracket) {
      var accessory = accessories.shim;
      var card = els.cardShim;
      var checkboxSlot = els.stepShimCheckbox;
      var toggleSlot = els.stepShimToggle;

      if (bracket.badWithLockingShim === true) {
        state.shim = { included: false, variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML =
          '<p class="fnd-bundle__card-note">Unfortunately this bracket doesn\'t play nice with the locking shim — it locks at a steep angle, so we\'re leaving it out.' +
          (accessory ? ' <a href="' + escapeHtml(accessory.productUrl) + '">View locking shim anyway</a>' : '') +
          '</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
        return;
      }

      if (accessory && accessory.variants.length) {
        var preselect = accessory.variants[0];
        state.shim = { included: true, variantId: preselect.id, sellable: true };
        card.className = 'fnd-bundle__card';
        card.innerHTML =
          '<p class="fnd-bundle__card-note">Our Locking Shim lets you LOCK your plate in the on-show position.</p>' +
          productCardHtml({
            image: preselect.image,
            title: accessory.title,
            price: preselect.priceFormatted,
            quickViewUrl: accessory.productUrl,
            variantId: preselect.id,
            images: accessory.images
          }) +
          (accessory.variants.length > 1 ?
            '<div class="fnd-bundle__card-footer">' +
              '<select class="fnd-bundle__card-select" data-role="variant">' +
                accessory.variants.map(function (v) {
                  return '<option value="' + v.id + '"' + (v.id === preselect.id ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' + escapeHtml(v.title) + (v.available ? '' : ' (sold out)') + '</option>';
                }).join('') +
              '</select>' +
            '</div>' : '');

        checkboxSlot.innerHTML = checkboxHtml(checkmarkIconHtml, 'include', true);
        toggleSlot.innerHTML = toggleBtnHtml(true);
        var variantSelect = card.querySelector('[data-role="variant"]');
        wireStepCheckbox(checkboxSlot.querySelector('input'), toggleSlot.querySelector('.fnd-bundle__step-toggle-btn'), card, function (checked) {
          state.shim.included = checked;
          if (variantSelect) variantSelect.disabled = !checked;
          updateSummary();
        });
        if (variantSelect) {
          variantSelect.addEventListener('change', function () {
            var opt = variantSelect.selectedOptions[0];
            var variant = opt ? accessory.variants.find(function (v) { return v.id === Number(opt.value); }) : null;
            state.shim.variantId = variant ? variant.id : null;
            if (variant) updateProductCardVisual(card, variant, accessory.productUrl);
            updateSummary();
          });
        }
      } else {
        state.shim = { included: false, variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML = '<p class="fnd-bundle__card-note">Locking shim isn\'t currently available.</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
      }
    }

    // ---- Indicator holders ----
    function indicatorTargetFor(type) {
      return type === 'static' ? accessories.indicatorStatic : type === 'moveable' ? accessories.indicatorMoveable : null;
    }

    function renderIndicatorsCard(bracket) {
      var card = els.cardIndicators;
      var checkboxSlot = els.stepIndicatorsCheckbox;
      var toggleSlot = els.stepIndicatorsToggle;
      var titleDc = String(bracket.productTitle || '').toLowerCase();

      if (bracket.doesNotWorkWithAnyIndicatorHolders === true) {
        state.indicators = { included: false, type: 'none', variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML = '<p class="fnd-bundle__card-note">This bracket isn\'t compatible with any of our indicator holders, so we can\'t offer one here.</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
        return;
      }

      // noIndicatorMounts is a tri-state, not a boolean: true -> needs the
      // add-on, false -> already has mounts ("not needed"), blank/unset ->
      // not confirmed either way, so this suggests the add-on too rather
      // than defaulting to "not needed" (same fix as the PDP's
      // block-bracket_blocks--indicator-holders.liquid).
      var defaultType = 'not_needed';
      if (bracket.noIndicatorMounts !== false) {
        defaultType = bracket.canFitStaticIndicatorHolders === false ? 'moveable' : 'static';
      }

      if (defaultType === 'not_needed') {
        var notNeededMsg = 'This bracket already has built-in indicator mounts, so you won\'t need the add-on.';
        if (bracket.canMountStockIndicators && bracket.canMountAftermarketIndicators) {
          notNeededMsg = 'This bracket already has holes to mount both stock and universal / aftermarket indicators, so you won\'t need the add-on.';
        } else if (bracket.canMountAftermarketIndicators) {
          notNeededMsg = 'This bracket only fits universal / aftermarket indicators — stock indicators won\'t fit — so you won\'t need the add-on.';
        } else if (bracket.canMountStockIndicators) {
          notNeededMsg = 'This bracket already has holes to mount stock indicators, so you won\'t need the add-on.';
        }
        state.indicators = { included: false, type: 'none', variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML = '<p class="fnd-bundle__card-note">' + notNeededMsg + '</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
        return;
      }

      var target = indicatorTargetFor(defaultType);
      if (!target || !target.variants.length) {
        state.indicators = { included: false, type: 'none', variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML = '<p class="fnd-bundle__card-note">Indicator holders for this bracket aren\'t currently available.</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
        return;
      }

      // An Integrated Rear Light already has its own built-in indicators,
      // so a separate holder would be redundant — checked here (rather
      // than earlier) so this still has a real "view anyway" link to
      // offer, same as the locking-shim incompatibility note above.
      // Returns whether an already-added selection just got cleared, so
      // the rear light's own add/remove handlers can tell the customer
      // (see renderRearLightExtra) rather than this happening silently
      // off-screen.
      if (state.rearLight.included) {
        var hadIndicators = state.indicators.included;
        state.indicators = { included: false, type: 'none', variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML =
          '<p class="fnd-bundle__card-note">Your Integrated Rear Light already includes built-in indicators, so we\'re leaving this out.' +
          ' <a href="' + escapeHtml(target.productUrl) + '">View indicator holders anyway</a>' +
          '</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
        return hadIndicators;
      }

      // No type switcher here: which type fits (static or moveable) is a
      // fixed fact about this bracket variant (canFitStaticIndicatorHolders),
      // same as the PDP's own indicator-holders block — letting the
      // customer switch to a type that doesn't actually fit would just
      // invite a bad purchase.
      var anyAvailable = target.variants.some(function (v) { return v.available; });
      if (!anyAvailable) {
        state.indicators = { included: false, type: 'none', variantId: null, sellable: false };
        card.className = 'fnd-bundle__card is-disabled';
        card.innerHTML =
          '<p class="fnd-bundle__card-note">' + escapeHtml(target.title) + ' for this bracket ' +
          (target.variants.length > 1 ? 'are' : 'is') + ' currently sold out.' +
          ' <a href="' + escapeHtml(target.productUrl) + '">View ' + escapeHtml(target.title) + '</a>' +
          '</p>';
        clearStepCheckbox(checkboxSlot, toggleSlot);
        return;
      }

      // Prefer an in-stock variant as the default — a bike-token match on a
      // sold-out variant would otherwise get preselected and silently block
      // adding to bundle (see the same fix applied to the rear light extra).
      var availableVariants = target.variants.filter(function (v) { return v.available; });
      var preselect = tokenMatchVariant(availableVariants, titleDc, availableVariants[0]);
      state.indicators = { included: true, type: defaultType, variantId: preselect.id, sellable: true };

      var typeNote = defaultType === 'static'
        ? 'This bracket takes our static indicator holders — they stay fixed and don\'t move with the plate.'
        : 'This bracket needs our moveable indicator holders instead, since the indicators have to move with the plate to stay properly aligned. Static holders won\'t fit here.';

      card.className = 'fnd-bundle__card';
      card.innerHTML =
        '<p class="fnd-bundle__card-note">' + typeNote + '</p>' +
        productCardHtml({
          image: preselect.image,
          title: target.title,
          price: preselect.priceFormatted,
          quickViewUrl: target.productUrl,
          variantId: preselect.id,
          images: target.images
        }) +
        (target.variants.length > 1 ?
          '<div class="fnd-bundle__card-footer">' +
            '<select class="fnd-bundle__card-select" data-role="variant"></select>' +
          '</div>' : '');

      if (target.variants.length > 1) populateIndicatorVariantSelect(card, defaultType, preselect.id);

      checkboxSlot.innerHTML = checkboxHtml(checkmarkIconHtml, 'include', true);
      toggleSlot.innerHTML = toggleBtnHtml(true);
      bindIndicatorControls(card, defaultType, checkboxSlot.querySelector('input'), toggleSlot.querySelector('.fnd-bundle__step-toggle-btn'));
    }

    function populateIndicatorVariantSelect(card, type, selectedId) {
      var variantSelect = card.querySelector('[data-role="variant"]');
      var target = indicatorTargetFor(type);
      if (!variantSelect || !target) return;
      variantSelect.innerHTML = target.variants.map(function (v) {
        return '<option value="' + v.id + '"' + (v.id === selectedId ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' + escapeHtml(v.title) + (v.available ? '' : ' (sold out)') + '</option>';
      }).join('');
    }

    function bindIndicatorControls(card, type, includeCb, includeToggleBtn) {
      var target = indicatorTargetFor(type);
      var variantSelect = card.querySelector('[data-role="variant"]');

      wireStepCheckbox(includeCb, includeToggleBtn, card, function (checked) {
        state.indicators.included = checked;
        if (variantSelect) variantSelect.disabled = !checked;
        updateSummary();
      });

      if (variantSelect) {
        variantSelect.addEventListener('change', function () {
          var opt = variantSelect.selectedOptions[0];
          var variant = opt && target ? target.variants.find(function (v) { return v.id === Number(opt.value); }) : null;
          state.indicators.variantId = variant ? variant.id : null;
          if (variant) updateProductCardVisual(card, variant, target.productUrl);
          updateSummary();
        });
      }
    }

    // ---- Extras (step 5 upsells) ----
    // Unlike the accessory steps above, these aren't tied to the resolved
    // bracket — same product/variants regardless of bike — so this only
    // needs to run once, at boot, rather than per-bracket. Each stays
    // collapsed to its one-line row until the customer taps its toggle,
    // deliberately kept out of the checkbox/toggle-slot pattern used above
    // since these need their own inline configuration UI, not just an
    // include/exclude flip.
    function renderShowPlateExtra() {
      var accessory = accessories.showPlate;
      var wrap = els.extraShowPlate;
      if (!wrap) return;
      if (!accessory || !accessory.variants.length) { wrap.hidden = true; return; }
      wrap.hidden = false;

      var lowestPrice = accessory.variants.reduce(function (min, v) { return Math.min(min, v.price); }, accessory.variants[0].price);
      els.extraShowPlatePrice.textContent = 'From ' + formatMoney(lowestPrice, moneyFormat);

      els.extraShowPlateLogo.innerHTML = accessory.variants.map(function (v) {
        return '<option value="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' + escapeHtml(v.title) + ' — ' + escapeHtml(v.priceFormatted) + (v.available ? '' : ' (sold out)') + '</option>';
      }).join('');

      function syncLogoPreview() {
        var opt = els.extraShowPlateLogo.selectedOptions[0];
        var variant = opt ? accessory.variants.find(function (v) { return v.id === Number(opt.value); }) : null;
        state.showPlate.variantId = variant ? variant.id : null;
        if (els.extraShowPlatePreviewLogo) els.extraShowPlatePreviewLogo.hidden = !(variant && variant.hasLogo);
      }

      // Reflects state.showPlate.included / whether the panel is open —
      // three states, one button: "Customise" (nothing configured yet),
      // "Cancel" (panel open, not yet added), "Remove" (added).
      // The panel's own open/close animates via the .is-open class (see
      // the grid-template-rows transition in the section's CSS) rather
      // than [hidden], which can't be transitioned.
      function isPanelOpen() {
        return els.extraShowPlatePanel.classList.contains('is-open');
      }

      function syncToggleLabel() {
        var cancelling = !state.showPlate.included && isPanelOpen();
        els.extraShowPlateToggle.textContent = state.showPlate.included
          ? 'Remove'
          : (cancelling ? 'Cancel' : 'Customise & Add');
        els.extraShowPlateToggle.dataset.included = state.showPlate.included ? 'true' : 'false';
        // "Cancel" isn't an add action — keep it neutral grey rather than
        // the green the (otherwise identical, not-yet-included) "Customise
        // & Add" state gets.
        els.extraShowPlateToggle.classList.toggle('is-cancel', cancelling);
      }

      // Full reset — used both for "Remove" on an already-added plate,
      // and to seed the initial state at boot.
      function reset() {
        state.showPlate = { included: false, variantId: accessory.variants[0].id, topLine: '', bottomLine: '', sellable: true };
        els.extraShowPlatePanel.classList.remove('is-open');
        els.extraShowPlateLogo.value = accessory.variants[0].id;
        els.extraShowPlateTop.value = '';
        els.extraShowPlateBottom.value = '';
        els.extraShowPlatePreviewTop.textContent = 'FLIP';
        els.extraShowPlatePreviewBottom.textContent = 'N DIP';
        syncLogoPreview();
        syncToggleLabel();
        updateSummary();
      }

      els.extraShowPlateToggle.addEventListener('click', function () {
        if (state.showPlate.included) {
          reset();
          return;
        }
        // Not added yet: this button only opens/closes the panel — typing
        // in it never adds anything on its own, "Add to bundle" below does.
        els.extraShowPlatePanel.classList.toggle('is-open');
        syncToggleLabel();
      });

      els.extraShowPlateAddBtn.addEventListener('click', function () {
        if (!els.extraShowPlateTop.reportValidity() || !els.extraShowPlateBottom.reportValidity()) return;
        state.showPlate.included = true;
        els.extraShowPlatePanel.classList.remove('is-open');
        syncToggleLabel();
        updateSummary();
      });

      els.extraShowPlateLogo.addEventListener('change', syncLogoPreview);

      els.extraShowPlateTop.addEventListener('input', function () {
        state.showPlate.topLine = els.extraShowPlateTop.value.trim();
        els.extraShowPlatePreviewTop.textContent = state.showPlate.topLine ? state.showPlate.topLine.toUpperCase() : 'FLIP';
      });

      els.extraShowPlateBottom.addEventListener('input', function () {
        state.showPlate.bottomLine = els.extraShowPlateBottom.value.trim();
        els.extraShowPlatePreviewBottom.textContent = state.showPlate.bottomLine ? state.showPlate.bottomLine.toUpperCase() : 'N DIP';
      });

      state.showPlate = { included: false, variantId: accessory.variants[0].id, topLine: '', bottomLine: '', sellable: true };
      syncLogoPreview();
      syncToggleLabel();
    }

    // Plate Extenders — nothing to configure, so the toggle button just
    // adds/removes it directly (same as the plate light / shim / indicator
    // steps further up), no expandable panel needed.
    function renderPlateExtendersExtra() {
      var accessory = accessories.plateExtenders;
      var wrap = els.extraPlateExtenders;
      if (!wrap) return;
      if (!accessory || !accessory.variants.length) { wrap.hidden = true; return; }
      wrap.hidden = false;

      var variant = accessory.variants[0];
      els.extraPlateExtendersPrice.textContent = formatMoney(variant.price, moneyFormat);
      state.plateExtenders = { included: false, variantId: variant.id, sellable: true };
      els.extraPlateExtendersToggle.dataset.included = 'false';

      els.extraPlateExtendersToggle.addEventListener('click', function () {
        state.plateExtenders.included = !state.plateExtenders.included;
        els.extraPlateExtendersToggle.textContent = state.plateExtenders.included ? 'Remove' : 'Add to bundle';
        els.extraPlateExtendersToggle.dataset.included = state.plateExtenders.included ? 'true' : 'false';
        updateSummary();
      });
    }

    // Integrated Rear Light — unlike the two extras above, this one is
    // bike-specific (a different light per bracket), so it's rendered
    // fresh every time a bracket resolves (called from selectBracket, like
    // the plate light / shim / indicator cards) rather than once at boot.
    //
    // The options come from custom.integrated_rear_light — a variant-level
    // list.variant_reference metafield set directly on the resolved
    // bracket VARIANT (not the product), so each bracket variant lists
    // exactly the rear light variant(s) that actually fit it. Often still
    // more than one (e.g. MT07's "2014 - 2024" bracket variant spans 3
    // rear light generations), so the customer may still need to pick —
    // this only narrows the choices to ones that are genuinely valid for
    // this exact bracket variant, rather than every variant of one
    // guessed product.
    function renderRearLightExtra(bracket) {
      var wrap = els.extraRearLight;
      if (!wrap) return;
      var options = bracket.rearLightOptions;

      // Reset per bracket — these are shared, static DOM elements (not
      // re-created per bike), so without this a note or an open panel left
      // over from the previous bike's light would carry straight over.
      if (els.extraRearLightNote) {
        els.extraRearLightNote.hidden = true;
        els.extraRearLightNote.textContent = '';
      }
      els.extraRearLightPanel.classList.remove('is-open');

      if (!options || !options.length) {
        wrap.hidden = true;
        state.rearLight = { included: false, variantId: null, sellable: false };
        return;
      }
      wrap.hidden = false;

      // Prefer an in-stock option as the default/opening selection — with
      // sold-out options mixed in (see MT07's "2025+" bracket variant,
      // whose one and only light option is currently sold out), defaulting
      // to options[0] regardless of stock could silently default to a dead
      // option. Falls back to options[0] only if every option is sold out.
      var first = options.find(function (o) { return o.available; }) || options[0];
      var anyAvailable = options.some(function (o) { return o.available; });
      var lowestPrice = options.reduce(function (min, o) { return Math.min(min, o.price); }, first.price);
      els.extraRearLightPrice.textContent = 'From ' + formatMoney(lowestPrice, moneyFormat);
      els.extraRearLightTitle.textContent = first.productTitle;
      els.extraRearLightModal.dataset.handle = first.productUrl;
      if (first.image) {
        els.extraRearLightImage.src = first.image;
        els.extraRearLightImage.hidden = false;
      } else {
        els.extraRearLightImage.hidden = true;
      }

      function optionLabel(o) {
        var label = o.productTitle;
        if (o.title && o.title !== 'Default Title' && o.title !== o.productTitle) label += ' — ' + o.title;
        return label;
      }

      els.extraRearLightVariant.innerHTML = options.map(function (o) {
        return '<option value="' + o.id + '"' + (o.available ? '' : ' disabled') + (o.id === first.id ? ' selected' : '') + '>' + escapeHtml(optionLabel(o)) + ' — ' + escapeHtml(o.priceFormatted) + (o.available ? '' : ' (sold out)') + '</option>';
      }).join('');

      // Only one option (no generation to choose between), so the year
      // <select> itself is hidden — but the panel (fitment disclaimer +
      // bullets) still opens on click either way. It's the one place that
      // confirms the exact fitment years, so skipping it for the
      // single-option case would mean some customers never see it at all.
      var hasChoice = options.length > 1;
      var variantFieldWrap = els.extraRearLightVariant.closest('.fnd-bundle__extra-field');
      if (variantFieldWrap) variantFieldWrap.hidden = !hasChoice;
      els.extraRearLightDesc.textContent = hasChoice
        ? 'Built-in indicators, brake light and running light — just pick your year below.'
        : 'Built-in indicators, brake light and running light — bolt straight on for a cleaner rear end.';

      function isPanelOpen() {
        return els.extraRearLightPanel.classList.contains('is-open');
      }

      function syncToggleLabel() {
        var cancelling = !state.rearLight.included && isPanelOpen();
        // Nothing sellable at all — don't invite a click that can only fail.
        if (!state.rearLight.included && !anyAvailable) {
          els.extraRearLightToggle.textContent = 'Sold out';
          els.extraRearLightToggle.disabled = true;
          els.extraRearLightToggle.dataset.included = 'false';
          els.extraRearLightToggle.classList.remove('is-cancel');
          return;
        }
        els.extraRearLightToggle.disabled = false;
        els.extraRearLightToggle.textContent = state.rearLight.included
          ? 'Remove'
          : (cancelling ? 'Cancel' : (hasChoice ? 'Select Light' : 'Check & Add'));
        els.extraRearLightToggle.dataset.included = state.rearLight.included ? 'true' : 'false';
        els.extraRearLightToggle.classList.toggle('is-cancel', cancelling);
      }

      // The rear light already has its own built-in indicators, so adding
      // one makes a separate holder redundant — renderIndicatorsCard
      // auto-clears it and reports back whether it just removed something
      // the customer had already added, so they're told right here rather
      // than needing to scroll back up to notice it's gone.
      function syncIndicatorsNote(justRemoved) {
        if (!els.extraRearLightNote) return;
        if (state.rearLight.included && justRemoved) {
          els.extraRearLightNote.textContent = '✓ Indicator holders removed — this light already includes them.';
          els.extraRearLightNote.hidden = false;
        } else {
          els.extraRearLightNote.hidden = true;
          els.extraRearLightNote.textContent = '';
        }
      }

      // Shows the currently-selected option's own "This part fits:" bullets
      // (custom.bullet_fitment_table, same data the light's own product page
      // shows) so the customer can actually verify it against their bike
      // right here — the bracket's own variant bucket can be coarser than
      // the light's, so the dropdown label alone isn't always enough.
      function syncFitmentList() {
        if (!els.extraRearLightFitmentList) return;
        var current = options.find(function (o) { return o.id === state.rearLight.variantId; }) || first;
        var bullets = current.bulletFitmentTable;
        if (!bullets || !bullets.length) {
          els.extraRearLightFitmentList.hidden = true;
          els.extraRearLightFitmentList.innerHTML = '';
          return;
        }
        els.extraRearLightFitmentList.innerHTML = bullets.map(function (b) {
          return '<li>' + escapeHtml(b) + '</li>';
        }).join('');
        els.extraRearLightFitmentList.hidden = false;
      }

      function reset() {
        state.rearLight = { included: false, variantId: first.id, sellable: true };
        els.extraRearLightPanel.classList.remove('is-open');
        els.extraRearLightVariant.value = first.id;
        syncToggleLabel();
        syncFitmentList();
        syncIndicatorsNote(false);
        renderIndicatorsCard(bracket);
        updateSummary();
      }

      // Assigned (not addEventListener) since this whole function re-runs
      // every time a new bracket resolves, but these elements are static
      // markup, not re-created each time — addEventListener here would
      // stack a new listener on top of the last bike's on every reselect.
      els.extraRearLightToggle.onclick = function () {
        if (state.rearLight.included) {
          reset();
          return;
        }
        // Always opens the panel (fitment disclaimer + bullets), even with
        // only one option to add — "Add to bundle" inside it is the only
        // thing that actually commits it, single option or not.
        els.extraRearLightPanel.classList.toggle('is-open');
        syncToggleLabel();
      };

      els.extraRearLightAddBtn.onclick = function () {
        var opt = els.extraRearLightVariant.selectedOptions[0];
        if (!opt || opt.disabled) return;
        state.rearLight.variantId = Number(opt.value);
        state.rearLight.included = true;
        els.extraRearLightPanel.classList.remove('is-open');
        syncToggleLabel();
        syncIndicatorsNote(renderIndicatorsCard(bracket));
        updateSummary();
      };

      els.extraRearLightVariant.onchange = function () {
        var opt = els.extraRearLightVariant.selectedOptions[0];
        state.rearLight.variantId = opt ? Number(opt.value) : null;
        syncFitmentList();
      };

      state.rearLight = { included: false, variantId: first.id, sellable: true };
      syncToggleLabel();
      syncFitmentList();
    }

    // ---- Summary + add to cart ----
    function updateSummary() {
      var lines = [];
      if (state.bracket) {
        lines.push({ label: state.bracket.productTitle, variant: state.bracket.variantTitle, price: state.bracket.price, variantId: state.bracket.variantId, url: state.bracket.productUrl });
      }
      if (state.plateLight.included && state.plateLight.variantId) {
        var v = findVariant(accessories.plateLight, state.plateLight.variantId);
        if (v) lines.push({ label: accessories.plateLight.title, variant: v.title, price: v.price, variantId: v.id, url: accessories.plateLight.productUrl });
      }
      if (state.shim.included && state.shim.variantId) {
        var sv = findVariant(accessories.shim, state.shim.variantId);
        if (sv) lines.push({ label: accessories.shim.title, variant: sv.title, price: sv.price, variantId: sv.id, url: accessories.shim.productUrl });
      }
      if (state.indicators.included && state.indicators.variantId) {
        var indicatorAccessory = indicatorTargetFor(state.indicators.type);
        var iv = findVariant(indicatorAccessory, state.indicators.variantId);
        if (iv) lines.push({ label: indicatorAccessory.title, variant: iv.title, price: iv.price, variantId: iv.id, url: indicatorAccessory.productUrl });
      }
      if (state.showPlate.included && state.showPlate.variantId) {
        var spv = findVariant(accessories.showPlate, state.showPlate.variantId);
        if (spv) lines.push({ label: accessories.showPlate.title, variant: state.showPlate.topLine + ' / ' + state.showPlate.bottomLine, price: spv.price, variantId: spv.id, url: accessories.showPlate.productUrl });
      }
      if (state.plateExtenders.included && state.plateExtenders.variantId) {
        var pev = findVariant(accessories.plateExtenders, state.plateExtenders.variantId);
        if (pev) lines.push({ label: accessories.plateExtenders.title, variant: pev.title, price: pev.price, variantId: pev.id, url: accessories.plateExtenders.productUrl });
      }
      if (state.rearLight.included && state.rearLight.variantId && state.bracket && state.bracket.rearLightOptions) {
        var rlv = state.bracket.rearLightOptions.find(function (o) { return o.id === state.rearLight.variantId; });
        if (rlv) lines.push({ label: rlv.productTitle, variant: rlv.title, price: rlv.price, variantId: rlv.id, url: rlv.productUrl });
      }

      els.summaryLines.innerHTML = lines.map(function (l) {
        var showVariant = l.variant && l.variant !== 'Default Title' && l.variant !== l.label;
        var quickViewUrl = productUrlWithVariant(l.url, l.variantId);
        var titleTag = quickViewUrl
          ? '<modal-opener data-modal="#PopupModal-quickview" data-handle="' + escapeHtml(quickViewUrl) + '">' +
              '<button type="button" class="fnd-bundle__summary-line-title" aria-haspopup="dialog">' + escapeHtml(l.label) + '</button>' +
            '</modal-opener>'
          : '<span class="fnd-bundle__summary-line-title">' + escapeHtml(l.label) + '</span>';
        return (
          '<li class="fnd-bundle__summary-line">' +
            '<span class="fnd-bundle__summary-line-info">' +
              titleTag +
              (showVariant ? '<span class="fnd-bundle__summary-line-variant">' + escapeHtml(l.variant) + '</span>' : '') +
            '</span>' +
            '<span class="fnd-bundle__summary-line-price">' + escapeHtml(formatMoney(l.price, moneyFormat)) + '</span>' +
          '</li>'
        );
      }).join('');

      var total = lines.reduce(function (sum, l) { return sum + Number(l.price || 0); }, 0);
      els.summaryTotal.textContent = formatMoney(total, moneyFormat);
      els.addBtn.dataset.lines = JSON.stringify(lines.map(function (l) { return l.variantId; }));
    }

    function findVariant(accessory, id) {
      if (!accessory) return null;
      return accessory.variants.find(function (v) { return v.id === id; }) || null;
    }

    function addBundleToCart() {
      if (!state.bracket) return;

      var items = [{
        id: state.bracket.variantId,
        quantity: 1,
        properties: {
          'Bike Make': state.bracket.brand,
          'Bike Model': state.bracket.model,
          'Bike Year': state.bracket.year
        }
      }];

      function pushIfIncluded(slice, accessory) {
        if (slice.included && slice.variantId) {
          var v = findVariant(accessory, slice.variantId);
          if (v && v.available) {
            items.push({ id: v.id, quantity: 1 });
          }
        }
      }

      pushIfIncluded(state.plateLight, accessories.plateLight);
      pushIfIncluded(state.shim, accessories.shim);
      pushIfIncluded(state.indicators, indicatorTargetFor(state.indicators.type));
      pushIfIncluded(state.plateExtenders, accessories.plateExtenders);
      if (state.rearLight.included && state.rearLight.variantId && state.bracket.rearLightOptions) {
        var rearLightOption = state.bracket.rearLightOptions.find(function (o) { return o.id === state.rearLight.variantId; });
        if (rearLightOption && rearLightOption.available) {
          items.push({ id: rearLightOption.id, quantity: 1 });
        }
      }

      if (state.showPlate.included && state.showPlate.variantId) {
        var showPlateVariant = findVariant(accessories.showPlate, state.showPlate.variantId);
        if (showPlateVariant && showPlateVariant.available) {
          items.push({
            id: showPlateVariant.id,
            quantity: 1,
            properties: {
              'top-line': state.showPlate.topLine,
              'bottom-line': state.showPlate.bottomLine
            }
          });
        }
      }

      var defaultLabel = els.addBtnLabel.textContent;
      els.addBtn.setAttribute('aria-disabled', 'true');
      els.addBtnLabel.textContent = 'Adding…';

      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = { items: items, attributes: { 'Order source': 'Bundle Builder' } };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (resp) {
          if (resp.status) {
            els.addBtnLabel.textContent = resp.description || 'Could not add — try again';
            setTimeout(function () {
              els.addBtn.removeAttribute('aria-disabled');
              els.addBtnLabel.textContent = defaultLabel;
            }, 2500);
            return;
          }
          els.addBtnLabel.textContent = 'Added ✓';
          if (typeof window.fndTrack === 'function') {
            window.fndTrack('Bundle Builder Add To Cart', {
              bike: state.bracket.brand + ' ' + state.bracket.model,
              plate_light: state.plateLight.included ? 'yes' : 'no',
              locking_shim: state.shim.included ? 'yes' : 'no',
              indicators: state.indicators.included ? state.indicators.type : 'no'
            });
          }
          if (cart) {
            if (typeof cart.setActiveElement === 'function') cart.setActiveElement(els.addBtn);
            cart.renderContents(resp);
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          }
          setTimeout(function () {
            els.addBtn.removeAttribute('aria-disabled');
            els.addBtnLabel.textContent = defaultLabel;
          }, 1500);
        })
        .catch(function () {
          els.addBtnLabel.textContent = 'Try again';
          setTimeout(function () {
            els.addBtn.removeAttribute('aria-disabled');
            els.addBtnLabel.textContent = defaultLabel;
          }, 2000);
        });
    }

    function saveToStorage(bracket) {
      try {
        localStorage.setItem('selected_bike', JSON.stringify({
          brand: bracket.brand,
          model: bracket.model,
          year: bracket.year,
          url: bracket.productUrl,
          savedAt: Date.now()
        }));
      } catch (e) { /* storage unavailable */ }
    }

    function populateModelYear(brand, model) {
      els.model.innerHTML = '<option value="">Model ↓</option>' + modelsFor(brand).map(function (m) {
        return '<option value="' + escapeHtml(m) + '"' + (m === model ? ' selected' : '') + '>' + escapeHtml(m) + '</option>';
      }).join('');
      els.model.disabled = false;
    }

    function populateYears(brand, model, year) {
      var years = Array.from(new Set(
        fitments.filter(function (f) { return f.brand === brand && f.model === model; }).map(function (f) { return f.year; })
      )).sort(function (a, b) { return b - a; });
      els.year.innerHTML = '<option value="">Year ↓</option>' + years.map(function (y) {
        return '<option value="' + escapeHtml(y) + '"' + (String(y) === String(year) ? ' selected' : '') + '>' + escapeHtml(y) + '</option>';
      }).join('');
      els.year.disabled = false;
    }

    // Arriving from a bracket product page's "Build your bundle" button —
    // ?bundle_variant=<variant id> names the exact variant they were
    // looking at there, so we can jump straight to that bike instead of
    // making them re-pick brand/model/year (or falling back to whatever
    // bike was last saved in this browser). Looked up directly against
    // fitments by variant id rather than routing through matchesFor, so
    // it's the exact variant, not just any fitment sharing its brand/
    // model/year.
    function restoreFromVariant(variantId) {
      var fitment = fitments.find(function (f) { return String(f.variantId) === String(variantId); });
      if (!fitment) return false;
      var product = products[fitment.productIndex];
      if (!product) return false;

      els.brand.value = fitment.brand;
      populateModelYear(fitment.brand, fitment.model);
      els.model.value = fitment.model;
      populateYears(fitment.brand, fitment.model, fitment.year);
      els.year.value = String(fitment.year);

      els.clearBtn.hidden = false;
      els.noMatch.hidden = true;
      renderResolvedPanel(product, fitment);
      return true;
    }

    function restoreFromStorage() {
      var raw;
      try { raw = localStorage.getItem('selected_bike'); } catch (e) { return; }
      if (!raw) return;
      var saved;
      try { saved = JSON.parse(raw); } catch (e) { return; }
      if (!saved || !saved.brand || !saved.model || !saved.year) return;
      if (!brandsList().includes(saved.brand)) return;

      els.brand.value = saved.brand;
      populateModelYear(saved.brand, saved.model);
      if (!modelsFor(saved.brand).includes(saved.model)) return;
      els.model.value = saved.model;
      populateYears(saved.brand, saved.model, saved.year);
      els.year.value = String(saved.year);
      if (els.year.value !== String(saved.year)) return;

      applyMatches(matchesFor(saved.brand, saved.model, saved.year));
    }

    els.brand.innerHTML += brandsList().map(function (b) { return '<option value="' + escapeHtml(b) + '">' + escapeHtml(b) + '</option>'; }).join('');

    els.brand.addEventListener('change', function () {
      els.model.innerHTML = '<option value="">Model ↓</option>';
      els.year.innerHTML = '<option value="">Year ↓</option>';
      els.model.disabled = true;
      els.year.disabled = true;
      els.clearBtn.hidden = true;
      els.noMatch.hidden = true;
      resetDownstream();
      if (!els.brand.value) return;
      populateModelYear(els.brand.value, null);
    });

    els.model.addEventListener('change', function () {
      els.year.innerHTML = '<option value="">Year ↓</option>';
      els.year.disabled = true;
      els.clearBtn.hidden = true;
      els.noMatch.hidden = true;
      resetDownstream();
      if (!els.model.value) return;
      populateYears(els.brand.value, els.model.value, null);
    });

    els.year.addEventListener('change', function () {
      if (!els.year.value) return;
      applyMatches(matchesFor(els.brand.value, els.model.value, els.year.value));
    });

    els.clearBtn.addEventListener('click', function () {
      try { localStorage.removeItem('selected_bike'); } catch (e) { /* noop */ }
      // A ?bundle_variant= link from a product page's "Build your bundle"
      // button takes priority over localStorage on load (see restoreFromVariant
      // below) — without stripping it here too, refreshing this same URL
      // after Clear would just re-select the bike again.
      try {
        var clearedUrl = new URL(window.location.href);
        if (clearedUrl.searchParams.has('bundle_variant')) {
          clearedUrl.searchParams.delete('bundle_variant');
          window.history.replaceState({}, '', clearedUrl.pathname + clearedUrl.search + clearedUrl.hash);
        }
      } catch (e) { /* noop */ }
      els.brand.value = '';
      els.model.innerHTML = '<option value="">Model ↓</option>';
      els.year.innerHTML = '<option value="">Year ↓</option>';
      els.model.disabled = true;
      els.year.disabled = true;
      els.clearBtn.hidden = true;
      els.noMatch.hidden = true;
      resetDownstream();
    });

    renderShowPlateExtra();
    renderPlateExtendersExtra();

    els.addBtn.addEventListener('click', addBundleToCart);

    // ---- Thumbnail lightbox — shared by every card's thumbnail row ----
    var lightboxState = { images: [], index: 0 };

    function showLightboxImage() {
      els.lightboxImage.src = lightboxState.images[lightboxState.index];
      var hasMultiple = lightboxState.images.length > 1;
      els.lightboxPrev.hidden = !hasMultiple;
      els.lightboxNext.hidden = !hasMultiple;
    }

    function openLightbox(images, index) {
      if (!images || !images.length) return;
      lightboxState.images = images;
      lightboxState.index = index;
      showLightboxImage();
      els.lightbox.hidden = false;
    }

    function closeLightbox() {
      els.lightbox.hidden = true;
    }

    function stepLightbox(delta) {
      var len = lightboxState.images.length;
      lightboxState.index = (lightboxState.index + delta + len) % len;
      showLightboxImage();
    }

    root.addEventListener('click', function (evt) {
      var thumb = evt.target.closest('.fnd-bundle__thumb');
      if (!thumb) return;
      var container = thumb.closest('.fnd-bundle__thumbs');
      if (!container) return;
      var images;
      try { images = JSON.parse(container.dataset.images); } catch (e) { images = []; }
      openLightbox(images, Number(thumb.dataset.index));
    });

    els.lightboxClose.addEventListener('click', closeLightbox);
    els.lightboxPrev.addEventListener('click', function () { stepLightbox(-1); });
    els.lightboxNext.addEventListener('click', function () { stepLightbox(1); });
    els.lightbox.addEventListener('click', function (evt) {
      if (evt.target === els.lightbox) closeLightbox();
    });
    document.addEventListener('keydown', function (evt) {
      if (els.lightbox.hidden) return;
      if (evt.key === 'Escape') closeLightbox();
      if (evt.key === 'ArrowLeft') stepLightbox(-1);
      if (evt.key === 'ArrowRight') stepLightbox(1);
    });

    var thumbPeekResizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(thumbPeekResizeTimer);
      thumbPeekResizeTimer = setTimeout(applyThumbPeek, 150);
    });

    setTimeout(function () {
      var incomingVariant = new URLSearchParams(window.location.search).get('bundle_variant');
      if (!incomingVariant || !restoreFromVariant(incomingVariant)) {
        restoreFromStorage();
      }
    }, 300);
  }

  function boot() {
    document.querySelectorAll('[data-fnd-bundle]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
